import { FC, useContext, useEffect, useRef, useState } from "react";
import { ActionPlace, DynamicsCompany, IAction, IGraphUser, IGroup, ILog, RequestState } from "../types/ApiTypes";
import Loading from "../components/Loading";
import { useNavigate, useParams } from "react-router-dom";
import { GroupsContext } from "../context/GroupsContext";
import { toast } from "react-hot-toast";
import Heading from "../components/ui/Heading";
import Card from "../components/ui/Card";
import { AppContext } from "../context/AppContext";
import useApi from "../hooks/useApi";
import SpaceDetailsLogList from "../components/space/SpaceDetailsLogList";
import { useAccount } from "@azure/msal-react";
import Button from "../components/ui/Button";
import SpaceDetailsInfoList from "../components/space/SpaceDetailsInfoList";
import SpaceDetailsInfoListItem from "../components/space/SpaceDetailsInfoListItem";
import axios from "axios";
import Modal from "../components/ui/Modal";
import { AnimatePresence, motion } from "framer-motion";
import { FaTimes, FaTrash } from "react-icons/fa";
import SpaceDetailsMemberList from "../components/space/SpaceDetailsMemberList";
import SpaceActionsList from "../components/space/SpaceActionsList";
import Avatar from "../components/ui/Avatar";
import useAuthSocket from "../hooks/useAuthSocket";
import AlertMessage from "../components/ui/AlertMessage";
import SpaceDetailsActionButtons from "../components/space/SpaceDetailsActionButtons";
import SpaceDetailsStatus from "../components/space/SpaceDetailsStatus";
import SpaceDetailsHeartbeat from "../components/space/SpaceDetailsHeartbeat";
import handleToastError from "../helpers/handleToastError";

const SpaceDetails: FC = () => {
    const [space, setSpace] = useState<IGroup>();

    const [spaceOwner, setSpaceOwner] = useState<IGraphUser|undefined>();
    const [spaceRequester, setSpaceRequester] = useState<IGraphUser|undefined>();
    const [spaceSecOwner, setSpaceSecOwner] = useState<IGraphUser|undefined>();
    const [spaceCustomer, setSpaceCustomer] = useState<DynamicsCompany|undefined>();
    const [spaceProjectCaution, setSpaceProjectCaution] = useState(false);
    const [spaceEditable, setSpaceEditable] = useState(true);

    const [spaceLogs, setSpaceLogs] = useState<ILog[]>([]);
    const [spaceLogsLoading, setSpaceLogsLoading] = useState(false);
    const [removalConfirmationModal, setRemovalConfirmationModal] = useState(false);

    const [actions, setActions] = useState<IAction[]>([]);

    const groupCtx = useContext(GroupsContext);
    const appCtx = useContext(AppContext);
    const {fetchSpaceLogs, fetchSpaceById, updateSpaceRequest, deleteSpaceRequest, fetchActions, rerunSpaceRequest} = useApi();
    const params = useParams<{id: string}>();
    const navigate = useNavigate();
    const account = useAccount();
    const {socket} = useAuthSocket();

    useEffect(() => {
        (async () => {
            if(params.id && groupCtx.state.groups.length > 0) {
                let group = groupCtx.state.groups.find(x => x.requestId === params.id);
                if(!group) {
                    group = await fetchSpaceById(params.id);
                }
                if(group) {
                    setSpace(group);
                    setSpaceOwner(appCtx.state.user?.find(x => x.id === group!.primary_owner));
                    setSpaceSecOwner(appCtx.state.user?.find(x => x.id === group!.secondary_owner));
                    setSpaceRequester(appCtx.state.user?.find(x => x.id === group!.requester));
                } else {
                    toast.error('Kein Space mit ID: '+params.id+' wurde gefunden');
                    navigate('/not-found');
                }
            }
        })();
    },[params.id,groupCtx.state.groups,navigate,appCtx.state.user]);

    useEffect(() => {
        const customer = groupCtx.state.customers.find(x => x.accountid === space?.customer);
        console.log("Customer effect")
        if(customer) setSpaceCustomer(customer);
    },[groupCtx.state.customers, space, space?.customer])

    useEffect(() => {
        if(space && space.request_state === RequestState.AwaitsAcceptance) setSpaceEditable(true);
        else setSpaceEditable(false);
    },[space])

    useEffect(() => {
        if(space) {
            setSpaceLogsLoading(true);
            fetchSpaceLogs(space.requestId).then(res => {
                if(res) setSpaceLogs(res);
                setSpaceLogsLoading(false);
            })
        }
    },[space,fetchSpaceLogs])

    useEffect(() => {
        if(space) {
            fetchActions(ActionPlace.Details).then(res => {
                if(res) setActions(res);
            })
        }
    },[space,fetchActions])

    useEffect(() => {
        socket.off('NEW_LOG_CREATED');
        socket.on('NEW_LOG_CREATED',(log: ILog) => {
            if(space) {
                if (log.group && log.group.requestId === space.requestId) {
                    setSpaceLogs(prevState => {
                        if(prevState.length < 10) return [log,...prevState]
                        prevState.splice(prevState.length - 1, 1);
                        return [log, ...prevState];
                    });
                }
            }
        })
    },[socket,space])

    const canSeeSpaceMembers = space && (appCtx.state.roles.includes('ROLE_ADMIN') || (space.primary_owner === account?.localAccountId || space.secondary_owner === account?.localAccountId)) && space.request_state === RequestState.Completed;

    const onSecOwnerChanged = async (secOwnerId: string) => {
        if(!space) return;
        await toast.promise(updateSpaceRequest({
            secondary_owner: secOwnerId,
            requestId: space.requestId
        }),{
            loading: 'Die Bearbeitung läuft',
            success: (group) => {
                groupCtx.dispatch({type: 'REPLACE_GROUP', payload: group});
                return <span className='text-sm'>Der Stellvertreter wurde geändert</span>
            },
            error: (err) => handleToastError(err, 'Das Space konnte nicht bearbeitet werden')
        });
    }

    const onDescriptionChanged = async (description: string) => {
        if(!space) return;
        await toast.promise(updateSpaceRequest({
            description: description,
            requestId: space.requestId
        }),{
            loading: 'Die Bearbeitung läuft',
            success: (group) => {
                groupCtx.dispatch({type: 'REPLACE_GROUP', payload: group});
                return <span className='text-sm'>Die Beschreibung wurde geändert</span>
            },
            error: (err) => handleToastError(err, "Das Space konnte nicht bearbeitet werden")
        }, {
            duration: 10000
        });
    }

    const onProjectChanged = (project: string) => {
        if(!space) return;
        groupCtx.dispatch({type: 'REPLACE_GROUP', payload: {...space, project: project}});
        toast.success('Der Auftrag wurde bearbeitet');
        setSpaceProjectCaution(false);
    }

    const handleSpaceDelete = async () => {
        if(!space) return;
        await toast.promise(deleteSpaceRequest(space.requestId),{
            loading: 'Das Space wird gelöscht',
            success: (group) => {
                groupCtx.dispatch({type: 'REMOVE_GROUP', payload: group});
                navigate('/');
                return <span className='text-sm'>Das Space wurde gelöscht</span>
            },
            error: (err) => handleToastError(err, 'Das Space konnte nicht gelöscht werden')
        });
    }

    const rerunSpace = async () => {
        if(!space) return;
        await toast.promise(rerunSpaceRequest(space.requestId),{
            loading: 'Das Space wird erneut erstellt',
            success: () => {
                return <span className='text-sm'>Das erneute Erstellen des Spaces wurde getriggert</span>
            },
            error: (err) => handleToastError(err, "Das Space konnte nicht erneut erstellt werden")
        });
    }

    return (
        <motion.section initial={{
            opacity: 0
        }} exit={{
            opacity: 0
        }} animate={{
            opacity: 1
        }}>
            {space ? (
                <>
                    <Heading>
                        <Avatar seed={space.name} type='initials'/>
                        <span>{space.type} {space.name} - Übersicht</span>
                    </Heading>
                    <div className='grid grid-cols-1 lg:grid-cols-4 gap-4'>
                        <div className="lg:col-span-3 space-y-3">
                            <Card>
                                {actions.length > 0 && <SpaceActionsList actions={actions} spaceId={space.requestId}/>}
                                <div className='space-y-4'>
                                    <SpaceDetailsStatus space={space}/>
                                    {space.forced && (
                                      <AlertMessage severity='warning'><b>Die Erstellung dieses Spaces wurde forciert.</b><br/>Bitte denke daran, dass das Space immer noch akzeptiert werden muss.</AlertMessage>
                                    )}
                                    <SpaceDetailsInfoList>
                                        <SpaceDetailsInfoListItem title='Beschreibung' value={space.description} italic={true} editable={spaceEditable} type='text' onEditDone={onDescriptionChanged}/>
                                        <SpaceDetailsInfoListItem accepted={space.primary_accepted} withAvatar={space.primary_owner} title='Besitzer' value={spaceOwner ? spaceOwner.displayName : space.primary_owner}/>
                                        <SpaceDetailsInfoListItem accepted={space.secondary_accepted} withAvatar={space.secondary_owner} editable={spaceEditable && space.type !== 56} type='person' title='Stellvertreter' value={spaceSecOwner ? spaceSecOwner.displayName : space.secondary_owner} onEditDone={onSecOwnerChanged}/>
                                        <SpaceDetailsInfoListItem withAvatar={space.requester} title='Auftraggeber' value={spaceRequester ? spaceRequester.displayName : space.requester}/>
                                        <SpaceDetailsInfoListItem title='ID des Requests' value={space.requestId}/>
                                        {space.unit && (
                                            <SpaceDetailsInfoListItem title='Spacebereich' value={space.unit.name} type='unit'/>
                                        )}
                                        {space.customer && (
                                            <SpaceDetailsInfoListItem title='Kunde' value={space.customer} displayText={spaceCustomer?.name} type='customer'/>
                                        )}
                                        {(space.project || spaceProjectCaution) && (
                                            <SpaceDetailsInfoListItem title='Auftrag' caution={spaceProjectCaution} customerId={space.customer} value={space.project || ''} editable={spaceEditable} type='project' onEditDone={onProjectChanged}/>
                                        )}
                                        {appCtx.state.roles.includes('ROLE_ADMIN') && (
                                            <>
                                                <p>------------------</p>
                                                {space.aadGroupId && <SpaceDetailsInfoListItem title='AAD_GROUP_ID' value={space.aadGroupId}/>}
                                                {space.aadAccessPackageId && <SpaceDetailsInfoListItem title='AAD_ACCESS_PACKAGE_ID' value={space.aadAccessPackageId}/>}
                                                {space.aadGuestPolicyId && <SpaceDetailsInfoListItem title='AAD_GUEST_POLICY_ID' value={space.aadGuestPolicyId}/>}
                                                {space.aadMemberPolicyId && <SpaceDetailsInfoListItem title='AAD_MEMBER_POLICY_ID' value={space.aadMemberPolicyId}/>}
                                                {space.aadOwnerPolicyId && <SpaceDetailsInfoListItem title='AAD_OWNER_POLICY_ID' value={space.aadOwnerPolicyId}/>}
                                            </>
                                        )}
                                    </SpaceDetailsInfoList>
                                    {/* Space Action buttons */}
                                    <SpaceDetailsActionButtons space={space} onRemove={() => setRemovalConfirmationModal(true)} onSpaceRerun={rerunSpace}/>
                                </div>
                            </Card>
                            {canSeeSpaceMembers && (
                                <div className='grid lg:grid-cols-2 gap-y-4 lg:gap-4'>
                                    <SpaceDetailsMemberList spaceId={space.requestId}/>
                                </div>
                            )}
                        </div>
                        <div className="space-y-3">
                            <SpaceDetailsLogList logs={spaceLogs} loading={spaceLogsLoading}/>
                            {(space.request_state === RequestState.Completed || space.request_state === RequestState.Failed) && <SpaceDetailsHeartbeat space={space} onRemove={() => setRemovalConfirmationModal(true)}/>}
                        </div>
                    </div>
                    {/* Modals */}
                    <AnimatePresence>
                        {removalConfirmationModal && <Modal title='Bestätige die Löschung des Spaces' isOpen={removalConfirmationModal} onClose={() => setRemovalConfirmationModal(false)}>
                            <p className='text-sm'>Indem du dieses Space löscht, kann es nicht wiederherstellt werden. Alle Daten, die zu diesem Space gehören, werden ebenfalls gelöscht.</p>
                            <div className='flex justify-end items-center mt-3 space-x-3'>
                                <Button onClick={() => setRemovalConfirmationModal(false)}><FaTimes className='mr-1'/> Schließen</Button>
                                <Button className='bg-error-brighter hover:shadow-error-brighter/20' onClick={handleSpaceDelete}><FaTrash className='mr-1'/> Löschen</Button>
                            </div>
                        </Modal>}
                    </AnimatePresence>
                </>
            ) : (
                <Loading/>
            )}
        </motion.section>
    );
};

export default SpaceDetails;